import { Button, Col, Divider, Flex, Input, Row, Typography, Grid } from 'antd';
import React, { useMemo } from 'react';
import { FaRegEnvelope } from 'react-icons/fa';
import logo from "@/assets/images/logo.png";
import { Link } from 'react-router';
import useTranslation from '@/hooks/useTranslation';
import T from '@/components/common/T';

const { useBreakpoint } = Grid;

const BUTTON_STYLE = {
    backgroundColor: "#FF2D55",
    color: "#FFF",
    borderRadius: "5px"
};

const FOOTER_LINKS = [
    { to: '/about',             key: 'footer.nav.about' },
    { to: '/events',            key: 'footer.nav.walks' },
    { to: '/sponsors-partners', key: 'footer.nav.sponsors' },
];

// Lookup table keyed by breakpoint tier: xs | sm | md | lg
const LAYOUT_BY_TIER = {
    xs: { paddingInline: 16, paddingBlock: 24, logoSize: 40, titleLevel: 4, titleSize: 18, sectionTitleSize: 16, textSize: 13, gutter: [24, 32], inputSize: 'middle', flexDirection: 'column', textAlign: 'center', alignItems: 'center' },
    sm: { paddingInline: 24, paddingBlock: 32, logoSize: 50, titleLevel: 2, titleSize: 22, sectionTitleSize: 18, textSize: 14, gutter: [36, 36], inputSize: 'large',  flexDirection: 'row',    textAlign: 'left',   alignItems: 'flex-start' },
    md: { paddingInline: 32, paddingBlock: 40, logoSize: 50, titleLevel: 2, titleSize: 26, sectionTitleSize: 20, textSize: 14, gutter: [36, 36], inputSize: 'large',  flexDirection: 'row',    textAlign: 'left',   alignItems: 'flex-start' },
    lg: { paddingInline: 48, paddingBlock: 48, logoSize: 50, titleLevel: 2, titleSize: 32, sectionTitleSize: 20, textSize: 14, gutter: [36, 36], inputSize: 'large',  flexDirection: 'row',    textAlign: 'left',   alignItems: 'flex-start' },
};

const getTier = (sm, md, lg) => {
    if (!sm) return 'xs';
    if (!md) return 'sm';
    if (!lg) return 'md';
    return 'lg';
};

const getLayout = (sm, md, lg) => LAYOUT_BY_TIER[getTier(sm, md, lg)];

export const FooterLayout = () => {
    const screens = useBreakpoint();
    const { t } = useTranslation();
    const layout = useMemo(
        () => getLayout(screens.sm, screens.md, screens.lg),
        [screens.sm, screens.md, screens.lg]
    );

    return (
        <React.Fragment>
            {/* Main Footer Content */}
            <div style={{
                paddingInline: layout.paddingInline,
                paddingBlock: layout.paddingBlock
            }}>
                <Row gutter={layout.gutter} justify="space-between">
                    {/* Logo & Description */}
                    <Col xs={24} sm={24} md={24} lg={8}>
                        <Flex gap={16} vertical align={layout.alignItems}>
                            <Flex gap={16} align="center">
                                <Link to="/" style={{
                                    width: layout.logoSize,
                                    height: layout.logoSize,
                                    flexShrink: 0
                                }}>
                                    <img
                                        loading="lazy"
                                        src={logo}
                                        style={{ width: "100%", height: "100%" }}
                                        alt="Never Give Up Day"
                                    />
                                </Link>
                                <Typography.Title
                                    style={{
                                        marginBlock: 0,
                                        paddingBlock: 0,
                                        color: "#FFF",
                                        fontSize: layout.titleSize,
                                        lineHeight: 1.2
                                    }}
                                    level={layout.titleLevel}
                                >
                                    Never Give Up Day
                                </Typography.Title>
                            </Flex>
                            <Typography.Paragraph
                                strong
                                style={{
                                    marginBlock: 0,
                                    paddingBlock: 0,
                                    color: "#FFF",
                                    fontSize: layout.textSize,
                                    textAlign: layout.textAlign
                                }}
                            >
                                <T k="footer.description" />
                            </Typography.Paragraph>
                        </Flex>
                    </Col>

                    {/* Navigation Links */}
                    <Col xs={24} sm={12} md={12} lg={8}>
                        <Flex gap={16} vertical align={layout.alignItems}>
                            <Typography.Title
                                style={{
                                    marginBlock: 0,
                                    paddingBlock: 0,
                                    color: "#FFF",
                                    fontSize: layout.sectionTitleSize
                                }}
                                level={3}
                            >
                                Never Give Up Day
                            </Typography.Title>
                            <Flex gap={12} vertical align={layout.alignItems}>
                                {FOOTER_LINKS.map(({ to, key }) => (
                                    <Link
                                        key={to}
                                        to={to}
                                        style={{
                                            marginBlock: 0,
                                            paddingBlock: 0,
                                            color: "#FFF",
                                            fontSize: layout.textSize,
                                            transition: 'opacity 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.target.style.opacity = '0.7'}
                                        onMouseLeave={(e) => e.target.style.opacity = '1'}
                                    >
                                        {t(key)}
                                    </Link>
                                ))}
                            </Flex>
                        </Flex>
                    </Col>

                    {/* Newsletter */}
                    <Col xs={24} sm={12} md={12} lg={8}>
                        <Flex gap={16} vertical align={layout.alignItems}>
                            <Typography.Title
                                style={{
                                    marginBlock: 0,
                                    paddingBlock: 0,
                                    color: "#FFF",
                                    fontSize: layout.sectionTitleSize
                                }}
                                level={3}
                            >
                                <T k="footer.newsletter.title" />
                            </Typography.Title>
                            <Typography.Paragraph
                                style={{
                                    marginBlock: 0,
                                    paddingBlock: 0,
                                    color: "#FFF",
                                    fontSize: layout.textSize,
                                    textAlign: layout.textAlign
                                }}
                            >
                                <T k="footer.newsletter.description" />
                            </Typography.Paragraph>
                            <Flex gap={8} style={{ width: '100%' }}>
                                <Input
                                    size={layout.inputSize}
                                    placeholder={t("footer.newsletter.placeholder")}
                                    variant="outlined"
                                    style={{
                                        flex: 1,
                                        borderRadius: '5px'
                                    }}
                                />
                                <Button
                                    size={layout.inputSize}
                                    icon={<FaRegEnvelope />}
                                    style={{ ...BUTTON_STYLE }}
                                    aria-label="Subscribe to newsletter"
                                />
                            </Flex>
                        </Flex>
                    </Col>
                </Row>
            </div>

            {/* Divider */}
            <Divider
                style={{
                    borderColor: "#fff",
                    borderTopStyle: "inset",
                    marginBlock: 1
                }}
            />

            {/* Copyright */}
            <div style={{
                paddingInline: layout.paddingInline,
                paddingBlock: 16
            }}>
                <Flex
                    justify="center"
                    align="center"
                    gap={16}
                    wrap="wrap"
                    style={{
                        fontSize: layout.textSize,
                        color: "#FFF",
                        flexDirection: screens.sm ? 'row' : 'column',
                        textAlign: 'center'
                    }}
                >
                    <div><T k="footer.copyright" /></div>
                </Flex>
            </div>
        </React.Fragment>
    );
};
